import useDebouncedValue from "../hooks/useDebouncedValue";
import { useStateParams } from "../hooks/useStateParams";
import { isDefined } from "../utils/isDefined";
import { SortOrder, isSortOrder } from "./common/models";
import { ApiPaginatedData } from "./common/paginatedData";
import useAuthenticatedFetch from "./common/useAuthenticatedFetch";
import { useAuthenticatedApiRequest } from "./common/useAuthenticatedFetcher";
import { ApiResponse } from "./models/ApiResponse";
import {
    CreateEmissionsSourceDto,
    MakeMeasuredDto,
    EditMonthlyEstimatesDto,
    EmissionsSourceDetail,
    EmissionsSourceItems,
    EmissionsSourceOrderBy,
    EmissionsSourceSummary,
    isEmissionsSourceOrderBy,
    MakeSecondaryDto,
    MakePrimaryDto,
    EmissionsSourceAddRequiredEvidenceDto,
    UpdateEmissionsSourceDto,
    Sector,
    Portfolio,
} from "./models/EmissionsSource";

export type useEmissionsSourcesReturn = {
    emissionsSources: EmissionsSourceSummary[];
    pageNumber: number;
    pageSize: number;
    locationIds: number[];
    sectorIds: number[];
    portfolioIds: number[];
    scopes: number[];
    item: number[];
    type: number[];
    category: number[];
    dataStatus: number[];
    isMaterial?: boolean;
    search: string;
    orderBy: EmissionsSourceOrderBy;
    sortOrder: SortOrder;
    totalCount: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    isLoading: boolean;
    setPageNumber: (
        pageNumber: ((currentValue: number) => number) | number
    ) => void;
    setPageSize: (
        pageSize: ((currentValue: number) => number) | number
    ) => void;
    setLocationIds: (
        locations: ((currentValue: number[]) => number[]) | number[]
    ) => void;
    setSectorIds: (
        sectors: ((currentValue: number[]) => number[]) | number[]
    ) => void;
    setPortfolioIds: (
        portfolios: ((currentValue: number[]) => number[]) | number[]
    ) => void;
    setItem: (items: ((currentValue: number[]) => number[]) | number[]) => void;
    setType: (types: ((currentValue: number[]) => number[]) | number[]) => void;
    setCategory: (
        categories: ((currentValue: number[]) => number[]) | number[]
    ) => void;
    setScopes: (
        scopes: ((currentValue: number[]) => number[]) | number[]
    ) => void;
    setDataStatus: (
        dataStatus: ((currentValue: number[]) => number[]) | number[]
    ) => void;
    setIsMaterial: (
        isMaterial:
            | ((currentValue: boolean | undefined) => boolean | undefined)
            | boolean
            | undefined
    ) => void;
    setSearch: (
        searchQuery: ((currentValue: string) => string) | string
    ) => void;
    setOrderBy: (
        orderBy:
            | ((currentValue: EmissionsSourceOrderBy) => EmissionsSourceOrderBy)
            | EmissionsSourceOrderBy
    ) => void;
    setSortOrder: (
        sortOrder: ((currentValue: SortOrder) => SortOrder) | SortOrder
    ) => void;
};

const filterEmissionsSources = (
    emissionsSources: EmissionsSourceSummary[] | undefined,
    hasLinkedDataSource?: boolean
) => {
    if (!emissionsSources) return [];

    if (hasLinkedDataSource === undefined) return emissionsSources;

    return emissionsSources.filter(
        (emissionsSource) =>
            hasLinkedDataSource === !!emissionsSource.dataSourceId
    );
};

export const defaultPageIndex = 0;
export const defaultPageSize = 15;
export const maxPageSize = 100;

export type usePaginatedEmissionsSourcesOptions = {
    isMaterialOverride?: boolean;
    dataStatusOverrideIds?: number[];
    hasLinkedDataSource?: boolean;
    initialPageSize?: number;
};

export const usePaginatedEmissionsSources = (
    options?: usePaginatedEmissionsSourcesOptions
): useEmissionsSourcesReturn => {
    options ??= {
        isMaterialOverride: undefined,
        dataStatusOverrideIds: undefined,
        hasLinkedDataSource: undefined,
        initialPageSize: undefined,
    };

    const queryParamSuffix = "Es";
    const [pageNumber, setPageNumber] = useStateParams<number>(
        defaultPageIndex,
        "page" + queryParamSuffix,
        (s) => s.toString(),
        (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultPageIndex)
    );
    const [pageSize, setPageSize] = useStateParams<number>(
        options.initialPageSize ?? defaultPageSize,
        "pageSize" + queryParamSuffix,
        (s) => s.toString(),
        (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultPageSize)
    );
    const [locationIds, setLocationIds] = useStateParams<number[]>(
        [],
        "locationId" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );
    const [item, setItem] = useStateParams<number[]>(
        [],
        "item" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );
    const [type, setType] = useStateParams<number[]>(
        [],
        "type" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );
    const [category, setCategory] = useStateParams<number[]>(
        [],
        "category" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );
    const [scopes, setScopes] = useStateParams<number[]>(
        [],
        "scope" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );
    const [dataStatus, setDataStatus] = useStateParams<number[]>(
        [],
        "dataStatus" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );
    const [isMaterial, setIsMaterial] = useStateParams<boolean | undefined>(
        options.isMaterialOverride ?? undefined,
        "isMaterial" + queryParamSuffix,
        (s) => (s ? s.toString() : ""),
        (s) => !!s
    );
    const [search, setSearch] = useStateParams<string>(
        "",
        "search" + queryParamSuffix,
        (s) => s,
        (s) => s
    );
    const [orderBy, setOrderBy] = useStateParams<EmissionsSourceOrderBy>(
        "Name",
        "orderBy" + queryParamSuffix,
        (s) => s,
        (s) => (isEmissionsSourceOrderBy(s) ? s : "Name")
    );
    const [sortOrder, setSortOrder] = useStateParams<SortOrder>(
        "asc",
        "sortOrder" + queryParamSuffix,
        (s) => s,
        (s) => (isSortOrder(s) ? s : "asc")
    );

    const [portfolioIds, setPortfolioIds] = useStateParams<number[]>(
        [],
        "portfolioId" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );

    const [sectorIds, setSectorIds] = useStateParams<number[]>(
        [],
        "sectorId" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );

    const debouncedSearchValue = useDebouncedValue(search, 500);

    const locationIdsQueryParam =
        locationIds.length > 0 ? `&locationIds=${locationIds}` : "";
    const portfolioIdsQueryParam =
        portfolioIds.length > 0 ? `&portfolioIds=${portfolioIds}` : "";
    const sectorIdsQueryParam =
        sectorIds.length > 0 ? `&sectorIds=${sectorIds}` : "";
    const scopesQueryParam = scopes.length > 0 ? `&scopes=${scopes}` : "";
    const itemQueryParam = item.length > 0 ? `&itemIds=${item}` : "";
    const typeQueryParam = type.length > 0 ? `&typeIds=${type}` : "";
    const categoryQueryParam =
        category.length > 0 ? `&categoryIds=${category}` : "";

    const dataStatusOverrideValues =
        options.dataStatusOverrideIds ?? dataStatus;
    const dataStatusQueryParam =
        dataStatusOverrideValues.length > 0
            ? `&dataStatuses=${dataStatusOverrideValues}`
            : "";

    const isMaterialQueryParam = isDefined(isMaterial)
        ? `&isMaterial=${isMaterial}`
        : "";

    const searchQueryParam =
        search !== "" ? `&searchTerm=${debouncedSearchValue}` : "";

    const { response, isLoading } = useAuthenticatedFetch<
        ApiResponse<ApiPaginatedData<EmissionsSourceSummary>>
    >(
        `api/emissions-sources?pageIndex=${pageNumber}` +
            `&pageSize=${pageSize}` +
            `&orderBy=${orderBy}` +
            `&orderBy=${orderBy}` +
            `&sortOrder=${sortOrder}` +
            `${locationIdsQueryParam}` +
            `${portfolioIdsQueryParam}` +
            `${sectorIdsQueryParam}` +
            `${scopesQueryParam}` +
            `${itemQueryParam}` +
            `${typeQueryParam}` +
            `${categoryQueryParam}` +
            `${dataStatusQueryParam}` +
            `${searchQueryParam}` +
            `${isMaterialQueryParam}`
    );

    return {
        emissionsSources: filterEmissionsSources(
            response?.content.items ?? [],
            options.hasLinkedDataSource
        ),
        isLoading,
        pageNumber,
        pageSize,
        locationIds,
        sectorIds,
        portfolioIds,
        item,
        type,
        category,
        scopes,
        dataStatus,
        isMaterial,
        search,
        orderBy,
        sortOrder,
        totalCount: response?.content.totalCount ?? 0,
        totalPages: response?.content.totalPages ?? 0,
        hasPreviousPage: response?.content.hasPreviousPage ?? false,
        hasNextPage: response?.content.hasNextPage ?? false,
        setPageNumber,
        setPageSize,
        setLocationIds,
        setItem,
        setType,
        setCategory,
        setScopes,
        setDataStatus,
        setIsMaterial,
        setSearch,
        setOrderBy,
        setSortOrder,
        setPortfolioIds,
        setSectorIds,
    };
};

export const useUniqueReferenceIsUnique = (): ((
    uniqueReference: string,
    id?: number
) => Promise<boolean>) => {
    const [request] = useAuthenticatedApiRequest<
        unknown,
        ApiPaginatedData<EmissionsSourceSummary>,
        string
    >(
        "GET",
        (uniqueReference) =>
            "api/emissions-sources" +
            `?uniqueReference=${uniqueReference}` +
            "&pageSize=1" +
            "&pageIndex=0"
    );

    return async (uniqueReference, id) => {
        const existingEmissionsSources = await request(
            undefined,
            uniqueReference
        );

        if (
            id &&
            existingEmissionsSources.success &&
            existingEmissionsSources.content.items.length === 1
        ) {
            return existingEmissionsSources.content.items[0].id === id;
        }

        return (
            existingEmissionsSources.success &&
            existingEmissionsSources.content.items.length === 0
        );
    };
};

export const useEmissionsSourceItems = () => {
    const { response, isLoading } = useAuthenticatedFetch<
        ApiResponse<EmissionsSourceItems>
    >("api/emissions-source-items");

    return {
        emissionsSourceItems: response?.content ?? null,
        isLoading,
    };
};

export const usePortfolios = () => {
    const { response, isLoading } =
        useAuthenticatedFetch<ApiResponse<Portfolio[]>>("api/portfolios");

    return {
        portfolios: response?.content ?? [],
        isLoading,
    };
};

export const useSectors = () => {
    const { response, isLoading } =
        useAuthenticatedFetch<ApiResponse<Sector[]>>("api/sectors");

    return {
        sectors: response?.content ?? [],
        isLoading,
    };
};

export type UseEmissionsSourceDetailReturn = {
    emissionsSource: EmissionsSourceDetail | undefined;
    isLoading: boolean;
    refetch: () => void;
};

export const useEmissionsSourceById = (
    id: number
): UseEmissionsSourceDetailReturn => {
    const { response, isLoading, refetch } = useAuthenticatedFetch<
        ApiResponse<EmissionsSourceDetail>
    >(`api/emissions-sources/${id}`);

    return {
        emissionsSource: response?.content,
        isLoading,
        refetch,
    };
};

export const useCreateEmissionsSource = () =>
    useAuthenticatedApiRequest<CreateEmissionsSourceDto, void>(
        "POST",
        "api/emissions-sources"
    );

export const useUpdateEmissionsSource = (id: number) =>
    useAuthenticatedApiRequest<UpdateEmissionsSourceDto, void>(
        "PUT",
        `api/emissions-sources/${id}`
    );

export const useEditMonthlyEstimates = (id: number) =>
    useAuthenticatedApiRequest<EditMonthlyEstimatesDto, void>(
        "PUT",
        `api/emissions-sources/${id}/estimates`
    );

export const useAddEmissionsSourceRequiredEvidence = (
    emissionsSourceId: number
) =>
    useAuthenticatedApiRequest<EmissionsSourceAddRequiredEvidenceDto, void>(
        "POST",
        `api/emissions-sources/${emissionsSourceId}/required-evidence-types`
    );

export const useRemoveEmissionsSourceRequiredEvidence = (
    emissionsSourceId: number,
    evidenceTypeId: number
) =>
    useAuthenticatedApiRequest(
        "DELETE",
        `api/emissions-sources/${emissionsSourceId}/required-evidence-types/${evidenceTypeId}`
    );

export const useMakeMeasured = (id: number) =>
    useAuthenticatedApiRequest<MakeMeasuredDto, void>(
        "PUT",
        `api/emissions-sources/${id}/make-measured`
    );

export const useMakeSecondary = (id: number) =>
    useAuthenticatedApiRequest<MakeSecondaryDto, void>(
        "PUT",
        `api/emissions-sources/${id}/make-secondary`
    );

export const useMakePrimary = (id: number) =>
    useAuthenticatedApiRequest<MakePrimaryDto, void>(
        "PUT",
        `api/emissions-sources/${id}/make-primary`
    );
